'use client';

import { Sheep } from './sheep-strip';

/**
 * Welcome-screen hero animation (Veronika 2026-06-13, tour-polish session).
 *
 * The sheep walks in from the far upper-right corner heading toward us at the
 * lower-left, growing bigger as she approaches (a dead-simple pixel-art "3D"
 * depth cue — same closer=bigger idea as the home meadow's two planes). On
 * arrival she hops twice, then stands and grazes forever. (A grass field was
 * tried and dropped — Veronika 2026-06-13 — so it's just the sheep + skeins.)
 *
 * Reuses the canonical pixel `Sheep` (its `.sheep-head` group is what dips to
 * graze, exactly like SheepStrip). One-shot approach+hop on the walker wrapper
 * (fill forwards holds the arrived pose); the graze loops forever on the head
 * with a delay so it only starts once she's settled. Honors reduced-motion:
 * the sheep simply sits at her arrived spot.
 */

// As she hops, balls of yarn in the six section-card colours tumble out of her
// and roll across the field (Veronika 2026-06-13). Two pop on the first hop
// (~2.95s), four more on the second (~3.5s) — "even more skeins". Each rolls
// (rotates) to a scattered resting spot; --dx/--dy/--spin drive one shared
// keyframe, so the colours + landing spots are pure data.
const SKEINS = [
  { color: 'var(--accent-projects)', dx: -16, dy: 0, spin: -560, delay: 2.95 },
  { color: 'var(--accent-tools)', dx: 40, dy: -2, spin: 600, delay: 3.0 },
  { color: 'var(--accent-yarn)', dx: 86, dy: 1, spin: -680, delay: 3.5 },
  { color: 'var(--accent-finished)', dx: 132, dy: -2, spin: 720, delay: 3.55 },
  { color: 'var(--accent-techniques)', dx: 176, dy: 1, spin: -760, delay: 3.6 },
  { color: 'var(--accent-ideas)', dx: 214, dy: -1, spin: 820, delay: 3.65 },
];

export function WelcomeSheep() {
  return (
    <div className="ws-field" aria-hidden="true">
      {SKEINS.map((s, i) => (
        <span
          key={`s${i}`}
          className="ws-skein"
          style={
            {
              background: s.color,
              animationDelay: `${s.delay}s`,
              '--dx': `${s.dx}px`,
              '--dy': `${s.dy}px`,
              '--spin': `${s.spin}deg`,
            } as React.CSSProperties
          }
        />
      ))}

      <div className="ws-walker">
        <Sheep width={54} />
      </div>

      <style jsx>{`
        .ws-field {
          position: relative;
          width: 100%;
          max-width: 320px;
          height: 116px;
        }
        .ws-walker {
          position: absolute;
          left: 0;
          bottom: 8px;
          transform-origin: bottom left;
          will-change: transform;
          animation: ws-approach 4s linear forwards;
        }
        /* Yarn balls that tumble out on the hops. Origin is under the sheep's
           belly; they pop up, then roll out to their scattered resting spot.
           Hidden (opacity 0) until their delay fires — so the approach stays
           clean. */
        .ws-skein {
          position: absolute;
          left: 26px;
          bottom: 7px;
          width: 11px;
          height: 11px;
          border-radius: 50%;
          opacity: 0;
          will-change: transform, opacity;
          animation: ws-skein 1.5s cubic-bezier(0.3, 0.7, 0.3, 1) forwards;
        }
        /* Gentle walking bob, only while she's approaching (finite), then she
           holds still to graze. The bob is scaled by the wrapper, so it reads
           tiny when she's far and full-size up close — correct perspective. */
        :global(.ws-walker > svg) {
          display: block;
          animation: ws-bob 0.5s ease-in-out 5;
        }
        :global(.ws-walker .sheep-head) {
          transform-origin: 6px 12px;
          animation: ws-graze 3.6s ease-in-out infinite;
          animation-delay: 4s;
        }

        /* From far upper-right + tiny → arrives lower-left + full size, then
           two hops (ease-out up, ease-in down for a little gravity). */
        @keyframes ws-approach {
          0% {
            transform: translate(210px, -68px) scale(0.3);
          }
          68% {
            transform: translate(0, 0) scale(1);
            animation-timing-function: ease-out;
          }
          76% {
            transform: translate(0, -11px) scale(1);
            animation-timing-function: ease-in;
          }
          83% {
            transform: translate(0, 0) scale(1);
            animation-timing-function: ease-out;
          }
          90% {
            transform: translate(0, -8px) scale(1);
            animation-timing-function: ease-in;
          }
          97%,
          100% {
            transform: translate(0, 0) scale(1);
          }
        }
        @keyframes ws-bob {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-1.5px);
          }
        }
        /* Pop up out of the sheep, then roll (rotate) out to the resting spot. */
        @keyframes ws-skein {
          0% {
            opacity: 0;
            transform: translate(0, -3px) scale(0.5) rotate(0deg);
          }
          12% {
            opacity: 1;
            transform: translate(calc(var(--dx) / 4), -13px) scale(1) rotate(calc(var(--spin) / 4));
          }
          100% {
            opacity: 1;
            transform: translate(var(--dx), var(--dy)) scale(1) rotate(var(--spin));
          }
        }
        /* Head dips to nibble the near grass, over and over. */
        @keyframes ws-graze {
          0%,
          35%,
          100% {
            transform: translateY(0) rotate(0deg);
          }
          50%,
          72% {
            transform: translateY(2px) rotate(10deg);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .ws-walker,
          :global(.ws-walker > svg),
          :global(.ws-walker .sheep-head) {
            animation: none;
          }
          /* Show the skeins at their resting spots, no motion. */
          .ws-skein {
            animation: none;
            opacity: 1;
            transform: translate(var(--dx), var(--dy));
          }
        }
      `}</style>
    </div>
  );
}
